import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { CurrencyOrEmpty } from '@plato/app-shared/components/currency-or-empty';
import { MaskedContent } from '@plato/app-shared/components/masked-content';
import { DataText } from '@plato/ui-legacy/components/typography/data-text';
import { NumberOrEmpty } from '@plato/ui-legacy/components/typography/number-or-empty';
import type { ColorThreshold } from '@plato/ui-legacy/components/typography/percentage-or-empty';
import { PercentageOrEmpty } from '@plato/ui-legacy/components/typography/percentage-or-empty';
import { Text } from '@plato/ui-legacy/components/ui/text';
import type { ColumnDef } from '@plato/ui/components/data-table';
import {
    InfoHint,
    InfoHintBody,
    InfoHintContent,
    InfoHintHeader,
    InfoHintLabel,
    InfoHintTitle,
    InfoHintTrigger,
} from '@plato/ui/components/info-hint';
import { type ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type z from 'zod';
import type { userVisitsOverviewSchema } from '../../../server/api/routers/plato-user/query/users/get-user-visits-overview.ts';
import { generateQueryParamsStringForActivity } from '../../activity-feed/hooks/use-activity-filters.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { TaskCreatedSource, TeamPageCellTooltipColumn } from '../../analytics/types/analytics-events.ts';
import { TaskColumnCell } from '../../tasks/components/task-column-cell.tsx';
import { ActivityCell } from '../components/activity-cell.tsx';
import { ChurnRiskFlag, InactiveFlag } from '../components/attention-flags.tsx';
import { InfoHintStatDivider, InfoHintStatGrid, InfoHintStatRow } from '../components/info-hint-stats.tsx';
import { TrendChip } from '../components/trend-chip.tsx';
import { getRoleLabel } from '../utils/get-role-label.ts';

type UserVisitsOverviewRow = z.infer<typeof userVisitsOverviewSchema>;

const coverageThresholds: ColorThreshold[] = [
    { min: 0.75, color: 'text-green-600' },
    { min: 0.4, color: 'text-orange-500' },
    { min: 0, color: 'text-red-500' },
];

function countWithShare(count: number, base: number): ReactNode {
    return base > 0 ? (
        <>
            {count} · <PercentageOrEmpty decimals={0}>{count / base}</PercentageOrEmpty>
        </>
    ) : (
        count
    );
}

/**
 * Columns for the manager-only Team page per-rep table — `taskSource`
 * attributes the per-row New Task button to the hosting page. Visit metrics
 * are consolidated into a "Customer Contacts" column with a hover breakdown,
 * plus recommendations and tasks (STEER-133).
 */
export type UserOverviewColumnId =
    | 'user'
    | 'revenue'
    | 'grossMargin'
    | 'gmPercent'
    | 'platoRevenue'
    | 'churnRisk'
    | 'opportunities'
    | 'customerCoverage'
    | 'revenueCoverage'
    | 'customerContacts'
    | 'insightEngagement'
    | 'tasks'
    | 'newTask';

type UserOverviewColumnDef = ColumnDef<UserOverviewColumnId, UserVisitsOverviewRow>;

export function useUserOverviewColumns(taskSource: TaskCreatedSource = 'team_user'): UserOverviewColumnDef[] {
    const { t } = useTranslation('team');
    const { canViewGlobalAnalytics } = useAppConfig();
    const sendEvent = useAnalytics();

    const trackCellTooltipOpened = useCallback(
        (column: TeamPageCellTooltipColumn) => (open: boolean) => {
            if (open) {
                sendEvent('team_page_cell_tooltip_opened', { column });
            }
        },
        [sendEvent],
    );

    return useMemo((): UserOverviewColumnDef[] => {
        const userColumn: UserOverviewColumnDef = {
            id: 'user',
            enableSorting: true,
            accessorFn: (row) => row.fullName,
            header: t('user_table__column_user'),
            cell: ({ row }) => {
                const roleLabel = getRoleLabel(t, row.original.userRole);

                return (
                    <div className="flex w-full items-center gap-1.5">
                        <div className="min-w-0 flex-1">
                            {canViewGlobalAnalytics ? (
                                <Link
                                    className="text-blue-700 hover:underline"
                                    to={`/activities?${generateQueryParamsStringForActivity(
                                        { userIds: [row.original.id] },
                                        { source: 'team' },
                                    )}`}
                                >
                                    {row.original.fullName}
                                </Link>
                            ) : (
                                row.original.fullName
                            )}
                            {roleLabel && (
                                <Text as="div" size="xs" className="text-neutral-500">
                                    {roleLabel}
                                </Text>
                            )}
                        </div>
                        {row.original.isInactive && <InactiveFlag />}
                        {row.original.hasHighChurnRisk && <ChurnRiskFlag />}
                    </div>
                );
            },
            size: 250,
        };

        const revenueColumn: UserOverviewColumnDef = {
            id: 'revenue',
            enableSorting: true,
            accessorKey: 'periodRevenue',
            meta: {
                aiChat: { label: t('user_table__column_revenue', { ns: 'team' }) },
                align: 'right',
                hint: t('user_table__tooltip_revenue'),
            },
            header: t('user_table__column_revenue'),
            cell: ({ row }) => {
                // No book at all (e.g. managers without rep assignments) — there is
                // nothing to sum, which is not the same as a book that sold 0 €.
                if (row.original.assignedCustomersCount === 0) {
                    return <div className="w-full text-right text-neutral-400">-</div>;
                }

                return (
                    <div className="w-full text-right">
                        <MaskedContent>
                            <DataText>
                                <CurrencyOrEmpty decimals={0}>{row.original.periodRevenue}</CurrencyOrEmpty>
                            </DataText>
                            <TrendChip
                                value={row.original.salesTrendPercent}
                                shouldHighlight={row.original.salesTrendShouldHighlight}
                            />
                        </MaskedContent>
                    </div>
                );
            },
            size: 150,
        };

        const grossMarginColumn: UserOverviewColumnDef = {
            id: 'grossMargin',
            enableSorting: true,
            accessorKey: 'periodGrossMargin',
            meta: {
                align: 'right',
                aiChat: {
                    label: t('user_table__column_gross_margin', { ns: 'team' }),
                    description: "Gross margin amount of the rep's assigned customers in the selected period",
                },
                hint: t('user_table__tooltip_gross_margin'),
            },
            header: () => <MaskedContent>{t('user_table__column_gross_margin')}</MaskedContent>,
            cell: ({ row }) => {
                if (row.original.assignedCustomersCount === 0) {
                    return <div className="w-full text-right text-neutral-400">-</div>;
                }

                return (
                    <div className="w-full text-right">
                        <MaskedContent>
                            <DataText>
                                <CurrencyOrEmpty decimals={0}>
                                    {row.original.periodGrossMargin}
                                </CurrencyOrEmpty>
                            </DataText>
                            <TrendChip
                                value={row.original.grossMarginTrendPercent}
                                shouldHighlight={row.original.grossMarginTrendShouldHighlight}
                            />
                        </MaskedContent>
                    </div>
                );
            },
            size: 130,
        };

        const gmPercentColumn: UserOverviewColumnDef = {
            id: 'gmPercent',
            enableSorting: true,
            accessorFn: (row) => row.gmPercent,
            sortUndefined: 'last',
            meta: { align: 'right', hint: t('user_table__tooltip_gm_percent') },
            header: () => <MaskedContent>{t('user_table__column_gm_percent')}</MaskedContent>,
            cell: ({ row }) => {
                if (row.original.assignedCustomersCount === 0 || row.original.gmPercent === undefined) {
                    return <div className="w-full text-right text-neutral-400">-</div>;
                }

                return (
                    <div className="w-full text-right">
                        <MaskedContent>
                            <DataText>
                                <PercentageOrEmpty decimals={1}>{row.original.gmPercent}</PercentageOrEmpty>
                            </DataText>
                            <TrendChip
                                value={row.original.gmPercentDeltaPp}
                                unit="points"
                                shouldHighlight={row.original.gmPercentDeltaShouldHighlight}
                            />
                        </MaskedContent>
                    </div>
                );
            },
            size: 120,
        };

        const platoRevenueColumn: UserOverviewColumnDef = {
            id: 'platoRevenue',
            enableSorting: true,
            accessorKey: 'revenueUplift',
            meta: {
                align: 'right',
                aiChat: {
                    label: t('user_table__column_plato_revenue', { ns: 'team' }),
                    description:
                        'Billed revenue generated after the rep marked an AI recommendation as interested. Only orders dated within the selected period count. The recommendation itself may be older',
                },
                hint: t('user_table__tooltip_plato_revenue'),
            },
            header: t('user_table__column_plato_revenue'),
            cell: ({ row }) => {
                // No INTERESTED markings ever → nothing attributable, honest empty state.
                if (row.original.revenueUpliftInsightsCount === 0) {
                    return <div className="w-full text-right text-neutral-400">-</div>;
                }

                return (
                    <div className="flex w-full items-center justify-end">
                        <InfoHint onOpenChange={trackCellTooltipOpened('plato_revenue')}>
                            <InfoHintTrigger>
                                <InfoHintLabel>
                                    <DataText>
                                        <CurrencyOrEmpty decimals={0}>
                                            {row.original.revenueUplift}
                                        </CurrencyOrEmpty>
                                    </DataText>
                                </InfoHintLabel>
                            </InfoHintTrigger>
                            <InfoHintContent className="max-w-none" side="bottom">
                                <InfoHintHeader>
                                    <InfoHintTitle>{t('user_table__column_plato_revenue')}</InfoHintTitle>
                                </InfoHintHeader>
                                <InfoHintBody>
                                    <InfoHintStatGrid>
                                        <InfoHintStatRow
                                            label={t('user_table__breakdown_total')}
                                            value={
                                                <CurrencyOrEmpty decimals={0}>
                                                    {row.original.revenueUplift}
                                                </CurrencyOrEmpty>
                                            }
                                            emphasized
                                        />
                                        <InfoHintStatDivider />
                                        <InfoHintStatRow
                                            label={t('user_table__plato_revenue_recommendations')}
                                            value={row.original.revenueUpliftInsightsCount}
                                        />
                                        {row.original.periodRevenue > 0 && (
                                            <InfoHintStatRow
                                                label={t('user_table__plato_revenue_share')}
                                                value={
                                                    <PercentageOrEmpty decimals={1}>
                                                        {row.original.revenueUplift /
                                                            row.original.periodRevenue}
                                                    </PercentageOrEmpty>
                                                }
                                            />
                                        )}
                                    </InfoHintStatGrid>
                                </InfoHintBody>
                            </InfoHintContent>
                        </InfoHint>
                    </div>
                );
            },
            size: 160,
        };

        const churnRiskColumn: UserOverviewColumnDef = {
            id: 'churnRisk',
            enableSorting: true,
            accessorKey: 'churnOpenValue',
            meta: {
                align: 'right',
                aiChat: {
                    label: t('user_table__column_churn_risk', { ns: 'team' }),
                    description:
                        "Summed revenue decline flagged by untriaged churn warnings on the rep's active customers, in tenant currency. Ignores the selected period",
                },
                hint: t('user_table__tooltip_churn_risk'),
            },
            header: t('user_table__column_churn_risk'),
            cell: ({ row }) => {
                if (row.original.assignedCustomersCount === 0) {
                    return <div className="w-full text-right text-neutral-400">-</div>;
                }

                // No open material warnings — plain 0 €, nothing to break down.
                if (row.original.churnOpenCount === 0) {
                    return (
                        <div className="w-full text-right">
                            <DataText>
                                <CurrencyOrEmpty decimals={0}>{row.original.churnOpenValue}</CurrencyOrEmpty>
                            </DataText>
                        </div>
                    );
                }

                return (
                    <div className="flex w-full items-center justify-end">
                        <InfoHint onOpenChange={trackCellTooltipOpened('churn_risk')}>
                            <InfoHintTrigger>
                                <InfoHintLabel>
                                    <DataText>
                                        <CurrencyOrEmpty decimals={0}>
                                            {row.original.churnOpenValue}
                                        </CurrencyOrEmpty>
                                    </DataText>
                                </InfoHintLabel>
                            </InfoHintTrigger>
                            <InfoHintContent className="max-w-none" side="bottom">
                                <InfoHintHeader>
                                    <InfoHintTitle>{t('user_table__column_churn_risk')}</InfoHintTitle>
                                </InfoHintHeader>
                                <InfoHintBody>
                                    <InfoHintStatGrid>
                                        <InfoHintStatRow
                                            label={t('user_table__breakdown_total')}
                                            value={
                                                <CurrencyOrEmpty decimals={0}>
                                                    {row.original.churnOpenValue}
                                                </CurrencyOrEmpty>
                                            }
                                            emphasized
                                        />
                                        <InfoHintStatDivider />
                                        <InfoHintStatRow
                                            label={t('user_table__churn_risk_count')}
                                            value={row.original.churnOpenCount}
                                        />
                                        <InfoHintStatRow
                                            label={t('user_table__churn_risk_customers')}
                                            value={row.original.churnOpenCustomerCount}
                                        />
                                    </InfoHintStatGrid>
                                </InfoHintBody>
                            </InfoHintContent>
                        </InfoHint>
                    </div>
                );
            },
            size: 160,
        };

        const opportunitiesColumn: UserOverviewColumnDef = {
            id: 'opportunities',
            enableSorting: true,
            accessorKey: 'opportunityOpenValue',
            meta: {
                align: 'right',
                aiChat: {
                    label: t('user_table__column_opportunities', { ns: 'team' }),
                    description:
                        "Open value of untriaged expansion opportunities for the rep's active customers. Ignores the selected period",
                },
                hint: t('user_table__tooltip_opportunities'),
            },
            header: t('user_table__column_opportunities'),
            cell: ({ row }) => {
                if (row.original.assignedCustomersCount === 0) {
                    return <div className="w-full text-right text-neutral-400">-</div>;
                }

                if (row.original.opportunityOpenCount === 0) {
                    return (
                        <div className="w-full text-right">
                            <DataText>
                                <CurrencyOrEmpty decimals={0}>
                                    {row.original.opportunityOpenValue}
                                </CurrencyOrEmpty>
                            </DataText>
                        </div>
                    );
                }

                return (
                    <div className="flex w-full items-center justify-end">
                        <InfoHint onOpenChange={trackCellTooltipOpened('opportunities')}>
                            <InfoHintTrigger>
                                <InfoHintLabel>
                                    <DataText>
                                        <CurrencyOrEmpty decimals={0}>
                                            {row.original.opportunityOpenValue}
                                        </CurrencyOrEmpty>
                                    </DataText>
                                </InfoHintLabel>
                            </InfoHintTrigger>
                            <InfoHintContent className="max-w-none" side="bottom">
                                <InfoHintHeader>
                                    <InfoHintTitle>{t('user_table__column_opportunities')}</InfoHintTitle>
                                </InfoHintHeader>
                                <InfoHintBody>
                                    <InfoHintStatGrid>
                                        <InfoHintStatRow
                                            label={t('user_table__breakdown_total')}
                                            value={
                                                <CurrencyOrEmpty decimals={0}>
                                                    {row.original.opportunityOpenValue}
                                                </CurrencyOrEmpty>
                                            }
                                            emphasized
                                        />
                                        <InfoHintStatDivider />
                                        <InfoHintStatRow
                                            label={t('user_table__opportunities_count')}
                                            value={row.original.opportunityOpenCount}
                                        />
                                        <InfoHintStatRow
                                            label={t('user_table__opportunities_customers')}
                                            value={row.original.opportunityOpenCustomerCount}
                                        />
                                    </InfoHintStatGrid>
                                </InfoHintBody>
                            </InfoHintContent>
                        </InfoHint>
                    </div>
                );
            },
            size: 160,
        };

        const customerCoverageColumn: UserOverviewColumnDef = {
            id: 'customerCoverage',
            enableSorting: true,
            accessorKey: 'customerCoveragePercent',
            meta: {
                align: 'right',
                aiChat: {
                    label: t('user_table__column_customer_coverage', { ns: 'team' }),
                    description:
                        'Share of assigned customers the rep visited in the last 12 months. Ignores the selected period',
                },
                hint: t('user_table__tooltip_customer_coverage'),
            },
            header: t('user_table__column_customer_coverage'),
            cell: ({ row }) => {
                const percent = row.original.customerCoveragePercent;
                const visited = row.original.visitedCustomersCount;
                const assigned = row.original.assignedCustomersCount;
                return (
                    <div className="text-right w-full">
                        <div>
                            <PercentageOrEmpty decimals={1} colorThresholds={coverageThresholds}>
                                {percent}
                            </PercentageOrEmpty>
                        </div>
                        <Text as="div" size="xs" className="text-neutral-500">
                            {/* Customer counts are hidden in Customer View. */}
                            <MaskedContent>
                                {visited} / {assigned}
                            </MaskedContent>
                        </Text>
                    </div>
                );
            },
            size: 180,
        };

        const revenueCoverageColumn: UserOverviewColumnDef = {
            id: 'revenueCoverage',
            enableSorting: true,
            accessorKey: 'revenueCoveragePercent',
            meta: {
                align: 'right',
                aiChat: {
                    label: t('user_table__column_revenue_coverage', { ns: 'team' }),
                    description:
                        "Share of assigned customers' billed revenue that comes from customers visited in the last 12 months. Ignores the selected period",
                },
                hint: t('user_table__tooltip_revenue_coverage'),
            },
            header: t('user_table__column_revenue_coverage'),
            cell: ({ row }) => {
                const percentage = (
                    <PercentageOrEmpty decimals={1} colorThresholds={coverageThresholds}>
                        {row.original.revenueCoveragePercent}
                    </PercentageOrEmpty>
                );

                // Nothing assigned → no € pair to show below.
                if (row.original.assignedRevenue === 0 && row.original.assignedCustomersCount === 0) {
                    return <DataText>{percentage}</DataText>;
                }

                return (
                    <div className="text-right w-full">
                        <div>{percentage}</div>
                        <Text as="div" size="xs" className="text-neutral-500">
                            {/* The € pair exposes revenue — hidden in Customer View. */}
                            <MaskedContent>
                                <CurrencyOrEmpty decimals={0}>{row.original.visitedRevenue}</CurrencyOrEmpty>{' '}
                                /{' '}
                                <CurrencyOrEmpty decimals={0}>{row.original.assignedRevenue}</CurrencyOrEmpty>
                            </MaskedContent>
                        </Text>
                    </div>
                );
            },
            size: 170,
        };

        const customerContactsColumn: UserOverviewColumnDef = {
            id: 'customerContacts',
            enableSorting: true,
            accessorFn: (row) => row.documentedVisitsCount + row.phoneCallCount + row.noteCount,
            meta: { align: 'right', hint: t('user_table__tooltip_contacts') },
            header: t('user_table__column_contacts'),
            cell: ({ row }) => (
                <ActivityCell
                    total={
                        row.original.documentedVisitsCount +
                        row.original.phoneCallCount +
                        row.original.noteCount
                    }
                    visits={row.original.documentedVisitsCount}
                    phoneCalls={row.original.phoneCallCount}
                    notes={row.original.noteCount}
                    visitsPerWeek={row.original.visitsPerWeek}
                    documentedVisits={row.original.documentedVisitsCount}
                    plannedVisits={row.original.plannedVisitsCount}
                    notesTotal={row.original.noteCount}
                    notesSubstantive={row.original.substantiveNoteCount}
                    onOpenChange={trackCellTooltipOpened('customer_contacts')}
                    feedHref={
                        canViewGlobalAnalytics
                            ? `/activities?${generateQueryParamsStringForActivity(
                                  { userIds: [row.original.id] },
                                  { source: 'team' },
                              )}`
                            : undefined
                    }
                />
            ),
            size: 160,
        };

        const recommendationsColumn: UserOverviewColumnDef = {
            id: 'insightEngagement',
            enableSorting: true,
            // Sort by total dispositioned recommendations (discussed + not relevant).
            accessorFn: (row) => row.triagedInsightsCount + row.notRelevantCount,
            meta: {
                align: 'right',
                hint: t('user_table__tooltip_recommendations'),
            },
            header: t('user_table__column_recommendations'),
            cell: ({ row }) => {
                const discussed = row.original.triagedInsightsCount;
                const notRelevant = row.original.notRelevantCount;
                const total = discussed + notRelevant;

                if (!row.original.hasRecordedOutcomes) {
                    return <div className="w-full text-right text-neutral-400">-</div>;
                }

                return (
                    <div className="flex w-full items-center justify-end">
                        <InfoHint onOpenChange={trackCellTooltipOpened('recommendations')}>
                            <InfoHintTrigger>
                                <InfoHintLabel>
                                    <NumberOrEmpty decimals={0}>{total}</NumberOrEmpty>
                                </InfoHintLabel>
                            </InfoHintTrigger>
                            <InfoHintContent className="max-w-none" side="bottom">
                                <InfoHintHeader>
                                    <InfoHintTitle>{t('user_table__column_recommendations')}</InfoHintTitle>
                                </InfoHintHeader>
                                <InfoHintBody>
                                    <InfoHintStatGrid>
                                        <InfoHintStatRow
                                            label={t('user_table__breakdown_total')}
                                            value={total}
                                            emphasized
                                        />
                                        {discussed > 0 && (
                                            <>
                                                <InfoHintStatDivider />
                                                <InfoHintStatRow
                                                    label={t('user_table__recommendations_discussed')}
                                                    value={countWithShare(discussed, total)}
                                                    emphasized
                                                />
                                                {row.original.interestedCount > 0 && (
                                                    <InfoHintStatRow
                                                        label={t('user_table__recommendations_interested')}
                                                        value={countWithShare(
                                                            row.original.interestedCount,
                                                            discussed,
                                                        )}
                                                        indented
                                                    />
                                                )}
                                                {row.original.noNeedCount > 0 && (
                                                    <InfoHintStatRow
                                                        label={t('user_table__recommendations_no_need')}
                                                        value={countWithShare(
                                                            row.original.noNeedCount,
                                                            discussed,
                                                        )}
                                                        indented
                                                    />
                                                )}
                                                {row.original.buysElsewhereCount > 0 && (
                                                    <InfoHintStatRow
                                                        label={t(
                                                            'user_table__recommendations_buys_elsewhere',
                                                        )}
                                                        value={countWithShare(
                                                            row.original.buysElsewhereCount,
                                                            discussed,
                                                        )}
                                                        indented
                                                    />
                                                )}
                                            </>
                                        )}
                                        {notRelevant > 0 && (
                                            <>
                                                <InfoHintStatDivider />
                                                <InfoHintStatRow
                                                    label={t('user_table__recommendations_not_relevant')}
                                                    value={countWithShare(notRelevant, total)}
                                                    emphasized
                                                />
                                                {row.original.incorrectCount > 0 && (
                                                    <InfoHintStatRow
                                                        label={t('user_table__recommendations_incorrect')}
                                                        value={countWithShare(
                                                            row.original.incorrectCount,
                                                            notRelevant,
                                                        )}
                                                        indented
                                                    />
                                                )}
                                                {row.original.normalCount > 0 && (
                                                    <InfoHintStatRow
                                                        label={t('user_table__recommendations_normal')}
                                                        value={countWithShare(
                                                            row.original.normalCount,
                                                            notRelevant,
                                                        )}
                                                        indented
                                                    />
                                                )}
                                            </>
                                        )}
                                    </InfoHintStatGrid>
                                </InfoHintBody>
                            </InfoHintContent>
                        </InfoHint>
                    </div>
                );
            },
            size: 160,
        };

        const tasksColumn: UserOverviewColumnDef = {
            id: 'tasks',
            enableSorting: true,
            accessorKey: 'tasksOpenCount',
            sortFn: (rowA, rowB) => {
                if (rowA.original.tasksOpenCount !== rowB.original.tasksOpenCount) {
                    return rowA.original.tasksOpenCount - rowB.original.tasksOpenCount;
                }
                return rowA.original.tasksCompletedCount - rowB.original.tasksCompletedCount;
            },
            meta: {
                aiChat: { label: t('user_table__column_tasks', { ns: 'team' }) },
                align: 'right',
                hint: t('user_table__tooltip_tasks'),
            },
            header: t('user_table__column_tasks'),
            cell: ({ row }) => {
                const open = row.original.tasksOpenCount;
                const overdue = row.original.tasksOverdueCount;
                const completed = row.original.tasksCompletedCount;

                if (open + overdue + completed === 0) {
                    return (
                        <div className="w-full text-right text-neutral-400">
                            {t('user_table__tasks_open', { value: 0 })}
                        </div>
                    );
                }

                return (
                    <div className="w-full text-right">
                        <div className="flex items-center justify-end">
                            <InfoHint onOpenChange={trackCellTooltipOpened('tasks')}>
                                <InfoHintTrigger>
                                    <InfoHintLabel>
                                        <span>{t('user_table__tasks_open', { value: open })}</span>
                                    </InfoHintLabel>
                                </InfoHintTrigger>
                                <InfoHintContent className="max-w-none" side="bottom">
                                    <InfoHintHeader>
                                        <InfoHintTitle>{t('user_table__column_tasks')}</InfoHintTitle>
                                    </InfoHintHeader>
                                    <InfoHintBody>
                                        <InfoHintStatGrid>
                                            <InfoHintStatRow
                                                label={t('user_table__tasks_row_open')}
                                                value={open}
                                                emphasized
                                            />
                                            <InfoHintStatRow
                                                label={t('user_table__tasks_row_overdue')}
                                                value={overdue}
                                                indented
                                            />
                                            <InfoHintStatDivider />
                                            <InfoHintStatRow
                                                label={t('user_table__tasks_row_completed')}
                                                value={completed}
                                            />
                                        </InfoHintStatGrid>
                                    </InfoHintBody>
                                </InfoHintContent>
                            </InfoHint>
                        </div>
                        <Text as="div" size="xs" className="text-neutral-500">
                            {t('user_table__tasks_completed', { value: completed })}
                        </Text>
                    </div>
                );
            },
            size: 140,
        };

        const newTaskColumn: UserOverviewColumnDef = {
            id: 'newTask',
            header: () => <span className="whitespace-nowrap">{t('user_table__column_new_task')}</span>,
            enableSorting: false,
            size: 80,
            cell: ({ row }) => <TaskColumnCell assignedToId={row.original.id} source={taskSource} />,
        };

        return [
            userColumn,
            revenueColumn,
            grossMarginColumn,
            gmPercentColumn,
            platoRevenueColumn,
            churnRiskColumn,
            opportunitiesColumn,
            recommendationsColumn,
            revenueCoverageColumn,
            customerCoverageColumn,
            customerContactsColumn,
            tasksColumn,
            newTaskColumn,
        ];
    }, [t, canViewGlobalAnalytics, taskSource, trackCellTooltipOpened]);
}
